import { useLazyQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useInterval, useTimeout } from "hooks/timers";
import { useCallback, useEffect, useRef, useState } from "react";
import { Config } from "services/configuration/eatSmartConfig";
import { selectQRCode, setQrCode } from "slices/accessSlice";
import { setInitialDishTypes } from "slices/dishesSlice";
import { selectIsLocalTransactionActive } from "slices/transactionSlice";
import type { AppDispatch } from "store";
import type { Fridge } from "types";
import type { LocalFridgeState } from "types/mainTypes";
import { FETCH_DISH_TYPES } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { getRandomNumberInRange } from "utils/math";
import { detectQrDrift } from "utils/qrDrift";
import { reloadForRecoveryNow } from "utils/recoveryReload";
import { getMilliSecondsToNextHour } from "utils/time";
import { deepOmit } from "utils/utils";
import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

interface Props {
	fridgeId: string;
}

export const handleSetDishTypes = (data: Fridge | LocalFridgeState, appDispatch: AppDispatch) => {
	const cleanedData: LocalFridgeState = deepOmit(data, ["__typename"]);
	appDispatch(setInitialDishTypes(cleanedData.dishes?.types));
	// Re-assert the access QR alongside prices. The hourly refetch defers while a local
	// transaction is active, so this can't overwrite the in-transaction nulled QR; guard on
	// truthy so a missing field never nulls a good QR.
	if (cleanedData.access?.qrCode?.code) {
		appDispatch(setQrCode(cleanedData.access.qrCode.code));
	}
};

const getInitialRefetchInterval = () => {
	const isProduction = import.meta.env.MODE === "production";
	const overrideVariable = import.meta.env.VITE_REFETCH_NEXT_HOUR_OVERRIDE;

	if (isProduction) {
		return getMilliSecondsToNextHour();
	}
	if (overrideVariable === undefined) {
		return getMilliSecondsToNextHour();
	}
	return Number.parseInt(import.meta.env.VITE_REFETCH_NEXT_HOUR_OVERRIDE, 10);
};

export const useRefetchDishTypesQuery = ({ fridgeId }: Props) => {
	const appDispatch = useAppDispatch();
	// A boolean, not the transaction itself: re-renders only on the running/idle edge,
	// not on every subscription frame that lands a fresh payload in the slot.
	const isLocalTransactionActive = useAppSelector(selectIsLocalTransactionActive);
	// The on-screen (delivered) access QR. Mirrored in a ref so the refetch callback reads the latest value
	// without adding it to the callback deps; synced in an effect, not during render. The callback only ever
	// runs post-commit (timers / deferred-refetch effect), so the effect has always run by then.
	const localQrCode = useAppSelector(selectQRCode);
	const localQrCodeRef = useRef(localQrCode);
	useEffect(() => {
		localQrCodeRef.current = localQrCode;
	}, [localQrCode]);
	const [refetchInterval, setRefetchInterval] = useState<number | null>(getInitialRefetchInterval());
	const [refetchDebounceTimeout, setRefetchDebounceTimeout] = useState<number | null>(null);
	const [isDeferredRefetchPending, setIsDeferredRefetchPending] = useState(false);
	const { initialStateQueryRefetchInterval, initialStateQueryRefetchDebounce } = Config.connectionSettings;
	const isMounted = useRef(true);

	// No onCompleted here: the ONLY Redux write for a refetch happens in handleRefetchDishTypes AFTER the
	// QR-drift check, so a drifted snapshot can never half-heal the display before we decide to reload.
	const [getDishTypes, { loading }] = useLazyQuery(FETCH_DISH_TYPES, {
		variables: { id: fridgeId },
		fetchPolicy: "network-only",
		refetchWritePolicy: "overwrite",
	});

	useEffect(
		() => () => {
			isMounted.current = false;
			setRefetchInterval(null);
			setRefetchDebounceTimeout(null);
			setIsDeferredRefetchPending(false);
		},
		[],
	);

	const handleRefetchDishTypes = useCallback(async () => {
		if (!isMounted.current) {
			return;
		}

		setIsDeferredRefetchPending(false);
		// Capture the on-screen (delivered) code BEFORE the fetch re-asserts it, so we can tell whether the
		// backend rotated past us while the subscription was silently not delivering.
		const localQrBeforeRefetch = localQrCodeRef.current;
		logToSentry(`Refetching prices: ${fridgeId}`, "info");

		const response = await getDishTypes();
		logToSentry(`Response from price refetch for fridge: ${fridgeId}`, "info");

		const beQrCode = response?.data?.fridge?.access?.qrCode?.code ?? null;
		// A drifted QR means a transaction happened that the subscription never delivered (zombie). The
		// subscription re-registers fine, so a restart wouldn't help; reload — the proven recovery. When the
		// storm guard suppresses the reload instead, fall through and heal the display with the fresh
		// snapshot, or the kiosk would keep showing the rotated-past (now invalid) QR until the next hour.
		if (
			(await detectQrDrift(beQrCode, localQrBeforeRefetch)) &&
			reloadForRecoveryNow(fridgeId, "qr-drift: missed transaction / zombie subscription")
		) {
			return;
		}

		if (response?.data) {
			handleSetDishTypes(response.data.fridge, appDispatch);
		} else {
			Sentry.captureException(new Error("Cannot refetch data"));
		}
	}, [fridgeId, getDishTypes, appDispatch]);

	// Resume a refetch that was deferred because a transaction was running. Owned here (not in
	// DeviceContext) so the provider doesn't need any transaction subscription at all.
	useEffect(() => {
		if (isDeferredRefetchPending && !isLocalTransactionActive) {
			handleRefetchDishTypes();
		}
	}, [isDeferredRefetchPending, isLocalTransactionActive, handleRefetchDishTypes]);

	// setup periodic refetch interval
	useInterval(() => {
		// Set the interval to the next hour for consistent hourly refetching
		setRefetchInterval(initialStateQueryRefetchInterval);
		// add debounce to the request not to overload the server
		const debounce = getRandomNumberInRange(0, initialStateQueryRefetchDebounce);
		setRefetchDebounceTimeout(debounce);
	}, refetchInterval);

	// setup timeout for debounced refetch
	useTimeout(() => {
		if (!isMounted.current) {
			return;
		}
		// Refetching mid-transaction would swap prices under the customer; defer until it ends.
		// (Covers local refills too — they change inventory just like orders.)
		if (isLocalTransactionActive) {
			setIsDeferredRefetchPending(true);
		} else {
			handleRefetchDishTypes();
		}
	}, refetchDebounceTimeout);

	return {
		loading: loading,
		handleRefetchDishTypes: handleRefetchDishTypes,
		isDeferredRefetchPending: isDeferredRefetchPending,
	};
};
